import { FormField, SelectField, TextareaField } from "@/components/common";
import { generateYearOptions } from "@/features/esg/utils/year-helpers";
import { useTran } from "@/hooks";
import { observer } from "mobx-react-lite";

const YEAR_OPTIONS = generateYearOptions();

const DisclosureEntryFields = observer(
  ({
    inputType,
    unitOptions = [],
    subEntryDefs = [],
    entry = {},
    errors = {},
    onChange,
    disabled = false,
  }) => {
    const { t } = useTran();

    const handleChange = (field) => (e) => onChange(field, e.target.value);
    const handleSelect = (field) => (value) => onChange(field, value);

    const valueInput = () => (
      <FormField
        label={t("Value")}
        type="number"
        value={entry.value || ""}
        onChange={handleChange("value")}
        placeholder="0"
        disabled={disabled}
        error={errors?.value}
      />
    );

    const textField = (label, value) => (
      <FormField
        label={t(label)}
        type="text"
        value={entry[value] || ""}
        onChange={handleChange(value)}
        placeholder={t(label)}
        disabled={disabled}
        error={errors?.[value]}
      />
    );

    const unitSelect = () => (
      <SelectField
        label={t("Unit")}
        options={unitOptions}
        value={entry.valueUnit || ""}
        onValueChange={handleSelect("valueUnit")}
        placeholder={t("Select unit")}
        disabled={disabled || unitOptions.length === 0}
        error={errors?.valueUnit}
      />
    );

    const yearSelect = () => (
      <SelectField
        label={t("Year")}
        options={YEAR_OPTIONS}
        value={entry.unit || ""}
        onValueChange={handleSelect("unit")}
        placeholder={t("Select year")}
        disabled={disabled}
        error={errors?.unit}
      />
    );

    switch (inputType) {
      case "simple":
      case "simpleLongText":
        return (
          <TextareaField
            label={t("Value")}
            value={entry.value || ""}
            onChange={handleChange("value")}
            rows={inputType === "simpleLongText" ? 4 : 2}
            disabled={disabled}
            error={errors?.value}
          />
        );

      case "value":
        return valueInput();

      case "valueUnit":
        return (
          <div className="grid grid-cols-2 gap-3">
            {valueInput()}
            {unitSelect()}
          </div>
        );

      case "valueUnitDenominator":
        return (
          <div className="grid grid-cols-3 gap-3">
            {valueInput()}
            {unitSelect()}
            <FormField
              label={t("Denominator")}
              type="text"
              value={entry.denominator || ""}
              onChange={handleChange("denominator")}
              placeholder={t("e.g. per employee")}
              disabled={disabled}
              error={errors?.denominator}
            />
          </div>
        );

      case "yearValue":
        return (
          <div className="grid grid-cols-2 gap-3">
            {yearSelect()}
            {valueInput()}
          </div>
        );

      case "yearValueUnit":
        return (
          <div className="grid grid-cols-3 gap-3">
            {yearSelect()}
            {valueInput()}
            {unitSelect()}
          </div>
        );

      case "yearValueCategory":
        return (
          <div className="grid grid-cols-3 gap-3">
            {yearSelect()}
            {textField("Category", "category")}
            {valueInput()}
          </div>
        );

      case "yearLocationValueUnit":
        return (
          <div className="grid grid-cols-4 gap-3">
            {yearSelect()}
            {textField("Location", "location")}
            {valueInput()}
            {unitSelect()}
          </div>
        );

      case "subEntries":
        return (
          <div className="space-y-3">
            {subEntryDefs.map((subDef, subIndex) => {
              const subEntry = entry.subEntries?.[subIndex] || {};
              return (
                <div
                  key={subDef.code || subIndex}
                  className="rounded-md border border-border p-3 space-y-2"
                >
                  <p className="text-xs font-medium text-muted-foreground">
                    {subDef.code && <span className="mr-1">{subDef.code}</span>}
                    {subDef.title || subDef.label}
                  </p>
                  <DisclosureEntryFields
                    inputType={subDef.inputType}
                    unitOptions={subDef.unitOptions || []}
                    entry={subEntry}
                    errors={errors?.[subIndex]}
                    onChange={(field, value) => {
                      const updatedSubEntries = [...(entry.subEntries || [])];
                      updatedSubEntries[subIndex] = {
                        subEntryId: subEntry.subEntryId || crypto.randomUUID(),
                        ...subEntry,
                        [field]: value,
                      };
                      onChange("subEntries", updatedSubEntries);
                    }}
                    disabled={disabled}
                  />
                </div>
              );
            })}
          </div>
        );

      default:
        return (
          <FormField
            label={t("Value")}
            type="text"
            value={entry.value || ""}
            onChange={handleChange("value")}
            disabled={disabled}
          />
        );
    }
  },
);

export default DisclosureEntryFields;
